
import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '../../../../common/element/Dialog';
import Tabs from '../../../../common/element/Tabs/Tabs';
import TabList from '../../../../common/element/Tabs/TabList';
import TabNav from '../../../../common/element/Tabs/TabNav';
import TabContent from '../../../../common/element/Tabs/TabContent';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import Select from '../../../../common/element/Select';
import Upload from '../../../../common/element/Upload';
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import CommonPhoneInput from '../../../../common/CommonPhoneInput/CommonPhoneInput';

import { clearCities, clearStates, getCityList, getCountryList, getStateList } from '../../../../redux/countryStateCity/cityStateCountrySlice';
import { selectCityList, selectCountryList, selectStateList } from '../../../../redux/countryStateCity/cityStateCountrySelector';
import { addCompany, updateCompany, clearAddCompany, clearUpdateCompany } from '../../../../redux/Companies/companiesSlice';
import { selectAddCompanyLoading, selectUpdateCompanyLoading } from '../../../../redux/Companies/companiesSelector';
import SweetAlertService from "../../../../common/SweetAlert/SweetAlert";
import { getCoachListAll } from '../../../../redux/Coach/coachSlice';
import { selectCoachListAll } from '../../../../redux/Coach/coachSelector';
import { selectUserData } from '../../../../redux/auth/authSelector';
import { ROLES } from '../../../../config/rolePermissions';
import { convertCompanyProspect } from '../../../../redux/ProspectCompany/companyProspectSlice';

const AddEditCompany = ({ dialogOpen, onDialogClose, companyData, isEdit, isConvert, refreshList }) => {
  const [tab, setTab] = useState("tab1");
  const dispatch = useDispatch();

  const countryList = useSelector(selectCountryList);
  const stateList = useSelector(selectStateList);
  const cityList = useSelector(selectCityList);
  const coachListAll = useSelector(selectCoachListAll);
  // eslint-disable-next-line no-unused-vars
  const addLoading = useSelector(selectAddCompanyLoading);
  // eslint-disable-next-line no-unused-vars
  const updateLoading = useSelector(selectUpdateCompanyLoading);
  const userData = useSelector(selectUserData);
  const userRole = userData?.role_name;

  // Convert API data to Select options format
  const countryOptions = countryList.map(country => ({
    value: country.country_id.toString(),
    label: country.country_name
  }));

  const stateOptions = stateList.map(state => ({
    value: state.state_id.toString(),
    label: state.state_name
  }));

  const cityOptions = cityList.map(city => ({
    value: city.city_id.toString(),
    label: city.city_name
  }));

  const numberOfEmployeesOptions = [
    { value: "10", label: "1-10" },
    { value: "50", label: "11-50" },
    { value: "200", label: "51-200" },
    { value: "500", label: "201-500" },
    { value: "500+", label: "500+" }
  ];

  const batchSizeOptions = [
    { value: "20", label: "Small (1-20)" },
    { value: "50", label: "Medium (21-50)" },
    { value: "51+", label: "Large (51+)" }
  ];

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" }
  ];

  const coachOptions = (coachListAll || []).map((item) => ({
    value: item.coach_id,
    label: item.name,
  }));

  // Initial form values
  const initialValues = {
    // Basic Details (Tab 1)
    company_logo: null,
    company_name: "",
    contact_person: "",
    company_admin_gender: null,
    contact_email: "",

    // Phone fields
    contact_phone: "",
    contact_phone_country: null,

    work_phone: "",
    work_phone_country: null,

    first_name: "",
    last_name: "",
    hr_gender: null,
    email: "",

    mobile_number: "",
    mobile_number_country: null,

    max_employees: null,

    // Address Details (Tab 2)
    address: "",
    addressLine2: "",
    city: null,
    state: null,
    pincode: "",

    phone_landline: "",
    phone_landline_country: null,

    country: null,
    Pan: "",
    Gstin: "",

    // Other Details (Tab 3)
    max_batch_size: null,
    notesAndDocuments: [
      { notes: "", documents: [] }
    ],
    social_links: [
      "",
      ""
    ],

    // Coach Details (Tab 4)
    coachIds: null
  };

  // Complete validation schema
  const completeValidationSchema = Yup.object({
    company_name: Yup.string().required("Company name is required"),
    contact_email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email address is required"),
    first_name: Yup.string().required("HR first name is required"),
    max_employees: Yup.object().nullable().required("Number of employees is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("HR email address is required")
  });

  // Validation schema for each tab
  const validationSchemas = {
    tab1: Yup.object({
      company_name: Yup.string().required("Company name is required"),
      contact_email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email address is required"),
      first_name: Yup.string().required("HR first name is required"),
      max_employees: Yup.object().nullable().required("Number of employees is required"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("HR email address is required")
    }),
    tab2: Yup.object({}),
    tab3: Yup.object({}),
    tab4: Yup.object({})
  };

  // --- Helper Functions ---

  const findOptionByValue = (options, value) => {
    return options.find(option => option.value === value) || null;
  };

  const getHRUserData = (companyData) => {
    if (companyData?.hr_user) {
      if (Array.isArray(companyData.hr_user) && companyData.hr_user.length > 0) {
        return companyData.hr_user[0];
      }
      if (typeof companyData.hr_user === 'object') {
        return companyData.hr_user;
      }
    }
    if (companyData?.hr) {
      return companyData.hr;
    }
    return null;
  };

  const splitAddress = (address) => {
    if (!address) return { line1: "", line2: "" };
    const lines = address.split('/n');
    return {
      line1: lines[0] || "",
      line2: lines.slice(1).join('/n') || ""
    };
  };

  const convertToSelectFormat = (data, idField, nameField) => {
    if (!data) return null;
    return {
      value: data[idField]?.toString(),
      label: data[nameField]
    };
  };

  const mapCoachesToSelectFormat = (coaches) => {
    if (!coaches || coaches.length === 0) return null;
    return coaches.map(coach => ({
      value: coach.coach_id,
      label: `${coach.first_name} ${coach.last_name}`.trim()
    }));
  };

  const processNotesAndDocuments = (notesAndDocs) => {
    if (!notesAndDocs || notesAndDocs.length === 0) {
      return [{ notes: "", documents: [] }];
    }

    return notesAndDocs.map(item => ({
      notes: item.notes || "",
      documents: (item.documents || []).map(doc => {
        if (typeof doc === 'string') {
          const fileName = doc.split('/').pop();
          return {
            name: fileName,
            url: doc,
            isExisting: true
          };
        }
        return doc;
      })
    }));
  };

  // --- Validation Logic ---

  const isPhoneValid = (value, country) => {
    if (!value) return true; // Optional field
    if (country && country.format) {
      const requiredLength = (country.format.match(/\./g) || []).length;
      return value.length >= requiredLength;
    }
    return true;
  };

  const handlePhoneChange = (fieldName, value, country, setFieldValue, setFieldTouched, setFieldError) => {
    setFieldValue(fieldName, value);
    setFieldValue(`${fieldName}_country`, country);
    setFieldTouched(fieldName, true, false);

    if (!value) {
      setFieldError(fieldName, undefined);
    } else {
      if (country && country.format) {
        const requiredLength = (country.format.match(/\./g) || []).length;
        if (value.length < requiredLength) {
          setFieldError(fieldName, "Invalid phone number");
        } else {
          setFieldError(fieldName, undefined);
        }
      }
    }
  };

  const validateTabFields = (targetTab, values, setFieldError) => {
    let isValid = true;

    if (targetTab === 'tab1' || targetTab === 'all') {
      const phones = ['work_phone', 'contact_phone', 'mobile_number'];
      phones.forEach(field => {
        if (!isPhoneValid(values[field], values[`${field}_country`])) {
          setFieldError(field, "Invalid phone number");
          isValid = false;
        }
      });
    }

    if (targetTab === 'tab2' || targetTab === 'all') {
      if (!isPhoneValid(values.phone_landline, values.phone_landline_country)) {
        setFieldError('phone_landline', "Invalid phone number");
        isValid = false;
      }
    }

    return isValid;
  };

  // --- Effects ---

  useEffect(() => {
    if (userRole === ROLES.ADMIN) {
      dispatch(getCoachListAll());
    }
    dispatch(getCountryList());

    if ((isEdit || isConvert) && companyData) {
      const countryId = companyData.country?.country_id || companyData.country;
      const stateId = companyData.state?.state_id || companyData.state;

      if (countryId) {
        dispatch(getStateList(countryId));
      }
      if (stateId) {
        dispatch(getCityList(stateId));
      }
    }
  }, [dispatch, isEdit, isConvert, companyData, userRole]);

  const handleDialogClose = (resetForm) => {
    resetForm();
    setTab("tab1");
    dispatch(clearStates());
    dispatch(clearCities());
    dispatch(clearAddCompany());
    dispatch(clearUpdateCompany());
    onDialogClose();
  };

  const handleTabClick = async (targetTab, validateForm, values, setTouched, setFieldError) => {
    const currentSchema = validationSchemas[tab];
    try {
      await currentSchema.validate(values, { abortEarly: false });
      if (validateTabFields(tab, values, setFieldError)) {
        setTab(targetTab);
      }
    } catch (error) {
      const touchedFields = {};
      if (error.inner) {
        error.inner.forEach(err => {
          touchedFields[err.path] = true;
        });
      }
      setTouched(touchedFields);
      validateTabFields(tab, values, setFieldError);
    }
  };

  const handleNext = async (validateForm, values, setTouched, setFieldError) => {
    const currentSchema = validationSchemas[tab];
    try {
      await currentSchema.validate(values, { abortEarly: false });
      if (validateTabFields(tab, values, setFieldError)) {
        const tabOrder = ["tab1", "tab2", "tab3", "tab4"];
        const currentIndex = tabOrder.indexOf(tab);
        if (currentIndex < tabOrder.length - 1) {
          setTab(tabOrder[currentIndex + 1]);
        }
      }
    } catch (error) {
      const touchedFields = {};
      if (error.inner) {
        error.inner.forEach(err => {
          touchedFields[err.path] = true;
        });
      }
      setTouched(touchedFields);
      validateTabFields(tab, values, setFieldError);
    }
  };

  const handleCountryChange = (selectedOption, setFieldValue) => {
    setFieldValue("country", selectedOption);
    setFieldValue("state", null);
    setFieldValue("city", null);
    dispatch(clearStates());
    dispatch(clearCities());

    if (selectedOption) {
      dispatch(getStateList(selectedOption.value));
    }
  };

  const handleStateChange = (selectedOption, setFieldValue) => {
    setFieldValue("state", selectedOption);
    setFieldValue("city", null);
    dispatch(clearCities());

    if (selectedOption) {
      dispatch(getCityList(selectedOption.value));
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    // Final check on ALL tabs before submit
    const isTab1Valid = validateTabFields('tab1', values, setFieldError);
    const isTab2Valid = validateTabFields('tab2', values, setFieldError);

    if (!isTab1Valid) {
      setTab('tab1');
      setSubmitting(false);
      return;
    }
    if (!isTab2Valid) {
      setTab('tab2');
      setSubmitting(false);
      return;
    }

    const formData = new FormData();

    // Add basic fields
    formData.append("company_name", values.company_name || "");
    formData.append("contact_person", values.contact_person || "");
    formData.append("contact_email", values.contact_email || "");
    formData.append("contact_phone", values.contact_phone || "");
    formData.append("work_phone", values.work_phone || "");

    // NEW Company Logo handling
    if (values.company_logo) {
      if (values.company_logo instanceof File) {
        formData.append("company_logo", values.company_logo);
      } else if (typeof values.company_logo === 'string') {
        formData.append("company_logo", values.company_logo);
      } else if (values.company_logo.url) { // fallback
        formData.append("company_logo", values.company_logo.url);
      }
    } else {
      formData.append("company_logo", "");
    }

    // NEW Gender Fields
    formData.append("company_admin_gender", values.company_admin_gender?.value || "");
    formData.append("hr_gender", values.hr_gender?.value || "");

    formData.append("first_name", values.first_name || "");
    formData.append("last_name", values.last_name || "");
    formData.append("email", values.email || "");
    formData.append("mobile_number", values.mobile_number || "");
    formData.append("max_employees", values.max_employees?.value || "");

    // Add address fields
    const addressLine1 = values.address || "";
    const addressLine2 = values.addressLine2 || "";

    let fullAddress = "";
    if (addressLine1 && addressLine2) {
      fullAddress = `${addressLine1}/n${addressLine2}`;
    } else if (addressLine1) {
      fullAddress = addressLine1;
    } else if (addressLine2) {
      fullAddress = addressLine2;
    }

    formData.append("address", fullAddress);
    formData.append("pincode", values.pincode || "");
    formData.append("phone_landline", values.phone_landline || "");
    formData.append("Pan", values.Pan || "");
    formData.append("Gstin", values.Gstin || "");
    formData.append("country", values.country?.value || "");
    formData.append("state", values.state?.value || "");
    formData.append("city", values.city?.value || "");

    formData.append("max_batch_size", values.max_batch_size?.value || "");

    const filteredNotesAndDocuments = values.notesAndDocuments.filter(item => {
      const hasNotes = item.notes && item.notes.trim() !== "";
      const hasDocuments = item.documents && item.documents.length > 0;
      return hasNotes || hasDocuments;
    });

    if (filteredNotesAndDocuments.length > 0) {
      filteredNotesAndDocuments.forEach((item, index) => {
        formData.append(`notesAndDocuments[${index}][notes]`, item.notes || "");
        if (item.documents && item.documents.length > 0) {
          item.documents.forEach((file) => {
            if (file instanceof File) {
              formData.append(`notesAndDocuments[${index}][documents]`, file);
            } else if (typeof file === 'string') {
              formData.append(`notesAndDocuments[${index}][documents]`, file);
            } else if (file.url) {
              formData.append(`notesAndDocuments[${index}][documents]`, file.url);
            }
          });
        }
      });
    }

    const filteredSocialLinks = values.social_links.filter(link => link && link.trim() !== "");
    if (filteredSocialLinks.length > 0) {
      filteredSocialLinks.forEach((link, index) => {
        formData.append(`social_links[${index}]`, link);
      });
    }

    if (values.coachIds && values.coachIds.length > 0) {
      values.coachIds.forEach((coach, index) => {
        formData.append(`coachIds[${index}]`, coach.value);
      });
    }

    let result;
    if (isConvert && companyData?.company_id) {
      result = await dispatch(convertCompanyProspect({ companyId: companyData.company_id, prospectData: formData }));
    } else if (isEdit && companyData?.company_id) {
      result = await dispatch(updateCompany({ id: companyData.company_id, formData }));
    } else {
      result = await dispatch(addCompany(formData));
    }

    setSubmitting(false);

    if (result?.meta?.requestStatus === "fulfilled") {
      SweetAlertService.showSuccess({
        title: "Success!",
        message: isEdit ? "Company updated successfully." : "Company added successfully.",
      });

      if (refreshList) {
        refreshList();
      }

      handleDialogClose(resetForm);
    }

    if (result?.meta?.requestStatus === "rejected") {
      SweetAlertService.showError({
        title: "Error!",
        message: result?.payload || `Failed to ${isEdit ? "update" : "add"} company. Please try again.`,
      });
    }
  };

  return (
    <Formik
      initialValues={(isEdit || isConvert) && companyData ? {
        company_logo: companyData.company_logo || null,
        company_name: companyData.company_name || "",
        contact_person: companyData.contact_person || "",
        contact_email: companyData.contact_email || companyData.email || "",
        contact_phone: companyData.contact_phone || "",
        work_phone: companyData.work_phone || "",

        // Map Gender for Edit/Convert
        company_admin_gender: findOptionByValue(genderOptions, companyData.company_admin_gender),
        hr_gender: findOptionByValue(genderOptions, companyData?.hr_gender || getHRUserData(companyData)?.gender),

        first_name: getHRUserData(companyData)?.first_name || "",
        last_name: getHRUserData(companyData)?.last_name || "",
        email: getHRUserData(companyData)?.email || "",
        mobile_number: getHRUserData(companyData)?.mobile_number || "",
        max_employees: findOptionByValue(numberOfEmployeesOptions, companyData.max_employees?.toString()),
        address: splitAddress(companyData.address).line1,
        addressLine2: splitAddress(companyData.address).line2,
        city: convertToSelectFormat(companyData.city, 'city_id', 'city_name'),
        state: convertToSelectFormat(companyData.state, 'state_id', 'state_name'),
        pincode: companyData.pincode || "",
        phone_landline: companyData.phone_landline || "",
        country: convertToSelectFormat(companyData.country, 'country_id', 'country_name'),
        Pan: companyData.Pan || "",
        Gstin: companyData.Gstin || "",
        max_batch_size: findOptionByValue(batchSizeOptions, companyData.max_batch_size?.toString()),
        notesAndDocuments: processNotesAndDocuments(companyData.notesAndDocuments),
        social_links: companyData.social_links && companyData.social_links.length > 0
          ? companyData.social_links
          : ["", ""],
        coachIds: mapCoachesToSelectFormat(companyData.coaches),
        // Initialize country objects for validation
        work_phone_country: null,
        contact_phone_country: null,
        mobile_number_country: null,
        phone_landline_country: null
      } : initialValues}
      validationSchema={completeValidationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, setFieldError, validateForm, isSubmitting, errors, touched, resetForm, setTouched, setFieldTouched }) => (
        <Dialog
          isOpen={dialogOpen}
          onClose={() => handleDialogClose(resetForm)}
          width={900}
          onRequestClose={() => handleDialogClose(resetForm)}
          bodyOpenClassName="overflow-hidden"
        >
          <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
            <div className="flex items-center">
              <span className="h-12 w-12 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 11H17.8C18.9201 11 19.4802 11 19.908 11.218C20.2843 11.4097 20.5903 11.7157 20.782 12.092C21 12.5198 21 13.0799 21 14.2V21M13 21V6.2C13 5.0799 13 4.51984 12.782 4.09202C12.5903 3.71569 12.2843 3.40973 11.908 3.21799C11.4802 3 10.9201 3 9.8 3H6.2C5.0799 3 4.51984 3 4.09202 3.21799C3.71569 3.40973 3.40973 3.71569 3.21799 4.09202C3 4.51984 3 5.0799 3 6.2V21M22 21H2M6.5 7H9.5M6.5 11H9.5M6.5 15H9.5" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              <div>
                <h5 className="mb-0 font-lato not-italic font-medium text-[16px] leading-[150%] text-[#101828]">
                  {isConvert ? "Convert Company" : isEdit ? "Edit Company" : "Add Company"}
                </h5>
                <p className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#475467] mb-0">
                  {isConvert ? "Convert prospect to company" : isEdit ? "Update company details" : "Create a new Company"}
                </p>
              </div>
            </div>

            <button
              onClick={() => handleDialogClose(resetForm)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <Form>
            <Tabs value={tab} variant="pill">
              <div className="px-2 mb-5">
                <div className="p-1 h-[45px] bg-[#FFF4E5] border border-[#F2F4F7] rounded-lg client-portal-tab">
                  <TabList>
                    <TabNav
                      value="tab1"
                      onClick={() => handleTabClick("tab1", validateForm, values, setTouched, setFieldError)}
                      style={{
                        backgroundColor: tab === 'tab1' ? '#ffffff' : 'transparent',
                        color: tab === 'tab1' ? '#344054' : '#667085',
                        fontWeight: tab === 'tab1' ? '600' : '500'
                      }}
                    >
                      Basic Details
                    </TabNav>
                    <TabNav
                      value="tab2"
                      onClick={() => handleTabClick("tab2", validateForm, values, setTouched, setFieldError)}
                      style={{
                        backgroundColor: tab === 'tab2' ? '#ffffff' : 'transparent',
                        color: tab === 'tab2' ? '#344054' : '#667085',
                        fontWeight: tab === 'tab2' ? '600' : '500'
                      }}
                    >
                      Address Details
                    </TabNav>
                    <TabNav
                      value="tab3"
                      onClick={() => handleTabClick("tab3", validateForm, values, setTouched, setFieldError)}
                      style={{
                        backgroundColor: tab === 'tab3' ? '#ffffff' : 'transparent',
                        color: tab === 'tab3' ? '#344054' : '#667085',
                        fontWeight: tab === 'tab3' ? '600' : '500'
                      }}
                    >
                      Other Details
                    </TabNav>
                    <TabNav
                      value="tab4"
                      onClick={() => handleTabClick("tab4", validateForm, values, setTouched, setFieldError)}
                      style={{
                        backgroundColor: tab === 'tab4' ? '#ffffff' : 'transparent',
                        color: tab === 'tab4' ? '#344054' : '#667085',
                        fontWeight: tab === 'tab4' ? '600' : '500'
                      }}
                    >
                      Coach Details
                    </TabNav>
                  </TabList>
                </div>
              </div>

              {/* Tab 1: Basic Details */}
              <TabContent value="tab1">
                <div className="px-6">

               

                  {/* Company Info */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Company info*
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Field
                          name="company_name"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter Company name here"
                        />
                        <ErrorMessage name="company_name" component="p" className="text-xs text-red-600 mb-2 mt-1" />
                      </div>
                      <div className="flex-1">
                        <Field
                          name="contact_person"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter Contact person name here"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Admin Gender & Email */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="Select Gender"
                          options={genderOptions}
                          value={values.company_admin_gender}
                          onChange={(selectedOption) => setFieldValue("company_admin_gender", selectedOption)}
                        />
                      </div>
                      <div className="flex-1">
                        <Field
                          name="contact_email"
                          type="email"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter Email address here"
                        />
                        <ErrorMessage name="contact_email" component="p" className="text-xs text-red-600 mb-2 mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* Work Phone and Phone Number */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <CommonPhoneInput
                          value={values.work_phone}
                          onChange={(phone, country) => handlePhoneChange("work_phone", phone, country, setFieldValue, setFieldTouched, setFieldError)}
                          onBlur={() => setFieldTouched("work_phone", true)}
                          placeholder="Enter Work phone number here"
                          error={touched.work_phone && errors.work_phone}
                        />
                      </div>
                      <div className="flex-1">
                        <CommonPhoneInput
                          value={values.contact_phone}
                          onChange={(phone, country) => handlePhoneChange("contact_phone", phone, country, setFieldValue, setFieldTouched, setFieldError)}
                          onBlur={() => setFieldTouched("contact_phone", true)}
                          placeholder="Enter Phone number here"
                          error={touched.contact_phone && errors.contact_phone}
                        />
                      </div>
                    </div>
                  </div>
   {/* Company Logo */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4 pt-2">
                      Company Logo
                    </label>
                    <div className="w-3/4 pl-2">
                      {values.company_logo ? (
                        <div className="border border-gray-300 rounded-lg p-3 w-full">
                          {(() => {
                            const isExisting = typeof values.company_logo === 'string';
                            const fileName = isExisting
                              ? values.company_logo.split('/').pop()
                              : values.company_logo.name;
                            const fileUrl = isExisting ? values.company_logo : null;
                            const fileSize = isExisting
                              ? 'Uploaded'
                              : `${(values.company_logo.size / 1024).toFixed(2)} KB`;

                            return (
                              <div className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group">
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div
                                    className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${isExisting ? 'cursor-pointer hover:opacity-80' : ''}`}
                                    style={{ backgroundColor: '#FF8E1C' }}
                                    onClick={() => {
                                      if (isExisting && fileUrl) {
                                        window.open(fileUrl, '_blank');
                                      }
                                    }}
                                    title={isExisting ? "Click to open image" : ""}
                                  >
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M11 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7l-5-5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      <path d="M11 2v5h5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  </div>
                                  <div
                                    className={`flex-1 min-w-0 ${isExisting ? 'cursor-pointer' : ''}`}
                                    onClick={() => {
                                      if (isExisting && fileUrl) {
                                        window.open(fileUrl, '_blank');
                                      }
                                    }}
                                  >
                                    <p
                                      className="text-sm font-medium text-gray-900 truncate hover:text-clip hover:whitespace-normal"
                                      title={fileName}
                                    >
                                      {fileName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {fileSize} {!isExisting && '- 100% uploaded'}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setFieldValue("company_logo", null)}
                                  className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove company logo"
                                >
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 4h4M4 7h12M16 7l-.867 9.355A2 2 0 0113.146 18H6.854a2 2 0 01-1.987-1.645L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <Upload
                          draggable
                          showList={false}
                          className="py-4 w-full h-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none"
                          onChange={(files) => {
                            let fileToUpload = null;

                            if (Array.isArray(files) && files.length > 0) {
                              fileToUpload = files[0];
                            } else if (files && !Array.isArray(files)) {
                              fileToUpload = files;
                            }

                            if (fileToUpload) {
                              const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
                              const extension = fileToUpload.name.split('.').pop().toLowerCase();
                              const allowedExts = ['jpg', 'jpeg', 'png'];

                              // Validate file format
                              if (allowedTypes.includes(fileToUpload.type) || allowedExts.includes(extension)) {
                                setFieldValue("company_logo", fileToUpload);
                              } else {
                                SweetAlertService.showError({
                                  title: "Invalid file format",
                                  message: "Please upload only JPG, JPEG, or PNG files.",
                                });
                              }
                            }
                          }}
                          multiple={false}
                          accept=".jpg, .jpeg, .png, image/jpeg, image/png"
                          maxFileSize={25 * 1024 * 1024}
                          fileList={[]}
                        >
                          <div className="flex flex-col items-center">
                            <svg width="40" height="40" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="3" y="3" width="40" height="40" rx="20" fill="#EAECF0" />
                              <rect x="3" y="3" width="40" height="40" rx="20" stroke="#F9FAFB" strokeWidth="6" />
                              <path d="M19.666 26.3333L22.9993 23M22.9993 23L26.3327 26.3333M22.9993 23V30.5M29.666 26.9524C30.6839 26.1117 31.3327 24.8399 31.3327 23.4167C31.3327 20.8854 29.2807 18.8333 26.7493 18.8333C26.5673 18.8333 26.3969 18.7383 26.3044 18.5814C25.2177 16.7374 23.2114 15.5 20.916 15.5C17.4642 15.5 14.666 18.2982 14.666 21.75C14.666 23.4718 15.3622 25.0309 16.4885 26.1613" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className="text-[14px] font-medium mt-2" style={{ color: '#FF8E1C' }}>
                              Click to upload file
                            </span>
                            <span className="text-[12px] font-medium text-[#475467] mt-1 font-lato">
                              Only JPG, JPEG, PNG allowed
                            </span>
                          </div>
                        </Upload>
                      )}
                    </div>
                  </div>
                  {/* HR Info */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      HR Info*
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Field
                          name="first_name"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter First name here"
                        />
                        <ErrorMessage name="first_name" component="p" className="text-xs text-red-600 mb-2 mt-1" />
                      </div>
                      <div className="flex-1">
                        <Field
                          name="last_name"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter Last name here"
                        />
                      </div>
                    </div>
                  </div>

                  {/* HR Gender & Email */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="Select HR Gender"
                          options={genderOptions}
                          value={values.hr_gender}
                          onChange={(selectedOption) => setFieldValue("hr_gender", selectedOption)}
                        />
                      </div>
                      <div className="flex-1">
                        <Field
                          name="email"
                          type="email"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Enter Email address here"
                        />
                        <ErrorMessage name="email" component="p" className="text-xs text-red-600 mb-2 mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* HR Mobile */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                    </label>
                    <div className="w-3/4 pl-2">
                      <CommonPhoneInput
                        value={values.mobile_number}
                        onChange={(phone, country) => handlePhoneChange("mobile_number", phone, country, setFieldValue, setFieldTouched, setFieldError)}
                        onBlur={() => setFieldTouched("mobile_number", true)}
                        placeholder="Enter Mobile number here"
                        error={touched.mobile_number && errors.mobile_number}
                      />
                    </div>
                  </div>

                  {/* Number of Employees */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Number of employees*
                    </label>
                    <div className="w-3/4 pl-2">
                      <Select
                        className="w-full h-9 select-options"
                        placeholder="Select number of employees"
                        options={numberOfEmployeesOptions}
                        value={values.max_employees}
                        onChange={(selectedOption) => setFieldValue("max_employees", selectedOption)}
                      />
                      <ErrorMessage name="max_employees" component="p" className="text-xs text-red-600 mb-2 mt-1" />
                    </div>
                  </div>
                </div>
              </TabContent>

              {/* Tab 2: Address Details */}
              <TabContent value="tab2">
                <div className="px-6">
                  {/* Country/State/City */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Country/State/City
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="Select Country"
                          options={countryOptions}
                          value={values.country}
                          onChange={(selectedOption) => handleCountryChange(selectedOption, setFieldValue)}
                        />
                      </div>
                      <div className="flex-1">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="State"
                          options={stateOptions}
                          value={values.state}
                          onChange={(selectedOption) => handleStateChange(selectedOption, setFieldValue)}
                          isDisabled={!values.country || stateOptions.length === 0}
                        />
                      </div>
                      <div className="flex-1">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="City"
                          options={cityOptions}
                          value={values.city}
                          onChange={(selectedOption) => setFieldValue("city", selectedOption)}
                          isDisabled={!values.state || cityOptions.length === 0}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Address */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Address
                    </label>
                    <div className="w-3/4 pl-2">
                      <Field
                        name="address"
                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline mb-3"
                        placeholder="Line 1"
                      />
                      <Field
                        name="addressLine2"
                        className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        placeholder="Line 2"
                      />
                    </div>
                  </div>



                  {/* Pincode/Landline - REPLACED LANDLINE WITH PHONE INPUT */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Pincode/Landline
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Field
                          name="pincode"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="Pincode"
                          onKeyPress={(e) => {
                            const charCode = e.which ? e.which : e.keyCode;
                            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <CommonPhoneInput
                          value={values.phone_landline}
                          onChange={(phone, country) => handlePhoneChange("phone_landline", phone, country, setFieldValue, setFieldTouched, setFieldError)}
                          onBlur={() => setFieldTouched("phone_landline", true)}
                          placeholder="Phone Landline"
                          error={touched.phone_landline && errors.phone_landline}
                        />
                      </div>
                    </div>
                  </div>

                  {/* PAN/GSTIN */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      PAN/GSTIN
                    </label>
                    <div className="w-3/4 pl-2 flex gap-3">
                      <div className="flex-1">
                        <Field
                          name="Pan"
                          type="text"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="PAN"
                        />
                      </div>
                      <div className="flex-1">
                        <Field
                          name="Gstin"
                          type="text"
                          className="h-9 pl-3 w-full input input-shadow font-lato not-italic font-medium text-[14px] leading-[150%] border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          placeholder="GSTIN"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </TabContent>

              {/* Tab 3: Other Details */}
              <TabContent value="tab3">
                <div className="px-6">
                  {/* Batch Size */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Batch Size
                    </label>
                    <div className="w-3/4 pl-2">
                      <Select
                        className="w-full h-9 select-options"
                        placeholder="Batch Size"
                        options={batchSizeOptions}
                        value={values.max_batch_size}
                        onChange={(selectedOption) => setFieldValue("max_batch_size", selectedOption)}
                      />
                    </div>
                  </div>

                  {/* Notes & Documents */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Notes & Documents
                    </label>
                    <div className="w-3/4 pl-2">
                      {values.notesAndDocuments.map((item, index) => (
                        <div key={index} className="mb-3">
                          <div className="flex items-stretch gap-3">
                            <div className="w-[40%]">
                              <Field
                                name={`notesAndDocuments.${index}.notes`}
                                as="textarea"
                                rows={4}
                                className="pl-3 pt-2 w-full h-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none"
                                placeholder="Notes"
                              />
                            </div>

                            <div className="w-[60%]">
                              {item.documents && item.documents.length > 0 ? (
                                <div className="border border-gray-200 rounded-lg p-3 h-full overflow-y-auto max-h-[200px]">
                                  {item.documents.map((file, fileIndex) => {
                                    // Check if it's an existing document (URL) or new file
                                    const isExisting = file.isExisting || typeof file === 'string';
                                    const fileName = isExisting
                                      ? (file.name || file.split('/').pop())
                                      : file.name;
                                    const fileUrl = isExisting ? (file.url || file) : null;
                                    const fileSize = isExisting
                                      ? 'Uploaded'
                                      : `${(file.size / 1024).toFixed(2)} KB`;

                                    return (
                                      <div
                                        key={fileIndex}
                                        className="flex items-center justify-between mb-2 last:mb-0 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                                      >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <div
                                            className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${isExisting ? 'cursor-pointer hover:opacity-80' : ''}`}
                                            style={{ backgroundColor: '#FF8E1C' }}
                                            onClick={() => {
                                              if (isExisting && fileUrl) {
                                                window.open(fileUrl, '_blank');
                                              }
                                            }}
                                            title={isExisting ? "Click to open document" : ""}
                                          >
                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M11 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7l-5-5z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              <path d="M11 2v5h5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </div>
                                          <div
                                            className={`flex-1 min-w-0 ${isExisting ? 'cursor-pointer' : ''}`}
                                            onClick={() => {
                                              if (isExisting && fileUrl) {
                                                window.open(fileUrl, '_blank');
                                              }
                                            }}
                                          >
                                            <p
                                              className="text-sm font-medium text-gray-900 truncate hover:text-clip hover:whitespace-normal"
                                              title={fileName}
                                            >
                                              {fileName}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                              {fileSize} {!isExisting && '- 100% uploaded'}
                                            </p>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedDocs = item.documents.filter((_, i) => i !== fileIndex);
                                            setFieldValue(`notesAndDocuments.${index}.documents`, updatedDocs);
                                          }}
                                          className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity"
                                          title="Remove document"
                                        >
                                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M8 4h4M4 7h12M16 7l-.867 9.355A2 2 0 0113.146 18H6.854a2 2 0 01-1.987-1.645L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <Upload
                                  draggable
                                  showList={false}
                                  className="pl-3 pt-2 w-full h-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline resize-none"
                                  onChange={(files) => setFieldValue(`notesAndDocuments.${index}.documents`, files)}
                                  multiple={true}
                                  maxFileSize={25 * 1024 * 1024}
                                  fileList={item.documents}
                                >
                                  <div className="flex flex-col items-center">
                                    <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect x="3" y="3" width="40" height="40" rx="20" fill="#EAECF0" />
                                      <rect x="3" y="3" width="40" height="40" rx="20" stroke="#F9FAFB" strokeWidth="6" />
                                      <path d="M19.666 26.3333L22.9993 23M22.9993 23L26.3327 26.3333M22.9993 23V30.5M29.666 26.9524C30.6839 26.1117 31.3327 24.8399 31.3327 23.4167C31.3327 20.8854 29.2807 18.8333 26.7493 18.8333C26.5673 18.8333 26.3969 18.7383 26.3044 18.5814C25.2177 16.7374 23.2114 15.5 20.916 15.5C17.4642 15.5 14.666 18.2982 14.666 21.75C14.666 23.4718 15.3622 25.0309 16.4885 26.1613" stroke="#475467" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="text-sm font-medium" style={{ color: '#FF8E1C' }}>
                                      Click to upload file
                                    </span>
                                  </div>
                                </Upload>
                              )}
                            </div>

                            {values.notesAndDocuments.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedNotesAndDocs = values.notesAndDocuments.filter((_, i) => i !== index);
                                  setFieldValue("notesAndDocuments", updatedNotesAndDocs);
                                }}
                                className="h-9 w-9 flex items-center justify-center rounded-lg hover:opacity-80 flex-shrink-0"
                                style={{ backgroundColor: '#FF8E1C' }}
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 4h4M4 7h12M16 7l-.867 9.355A2 2 0 0113.146 18H6.854a2 2 0 01-1.987-1.645L4 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <div style={{ backgroundColor: '#FFF4E5', padding: '12px 24px', borderRadius: '8px' }}>
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => setFieldValue("notesAndDocuments", [...values.notesAndDocuments, { notes: "", documents: [] }])}
                            className="text-sm font-medium flex items-center gap-2"
                            style={{ color: '#FF8E1C' }}
                          >
                            Add another field
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Social Media Links */}
                  <div className="flex mb-5">
                    <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                      Social Media Links
                    </label>
                    <div className="w-3/4 pl-2">
                      {values.social_links.map((link, index) => (
                        <div key={index} className="flex items-center gap-3 mb-3">
                          <Field
                            name={`social_links.${index}`}
                            className="h-9 pl-3 flex-1 input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            placeholder={index === 0 ? "www.facebook.com" : "www.linkedin.com"}
                          />
                          {values.social_links.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updatedLinks = values.social_links.filter((_, i) => i !== index);
                                setFieldValue("social_links", updatedLinks);
                              }}
                              className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-gray-50 flex-shrink-0"
                              style={{ backgroundColor: '#FF8E1C' }}
                            >
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 4h4M4 7h12M16 7l-.867 9.355A2 2 0 0113.146 18H6.854a2 2 0 01-1.987-1.645L4 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))}

                      <div style={{ backgroundColor: '#FFF4E5', padding: '12px 24px' }}>
                        <div className="flex justify-center">
                          <div className="pl-2">
                            <button
                              type="button"
                              onClick={() => setFieldValue("social_links", [...values.social_links, ""])}
                              className="text-sm font-medium flex items-center gap-2"
                              style={{ color: '#FF8E1C' }}
                            >
                              Add another field
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabContent>

              {/* Tab 4: Coach Details */}
              <TabContent value="tab4">
                <div className="px-6">
                  {Array.isArray(values.coachIds) ? (
                    <div className="flex mb-5">
                      <label className="font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] w-1/4">
                        Coach
                      </label>
                      <div className="w-3/4 pl-2">
                        <Select
                          className="w-full h-9 select-options"
                          placeholder="Select Coach"
                          options={coachOptions}
                          value={values.coachIds}
                          isMulti={true}
                          onChange={(selectedOption) => setFieldValue("coachIds", selectedOption)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16">
                      <p className="font-lato not-italic font-medium text-[14px] leading-[150%] text-center text-[#101828] mb-3">No Coach Assigned</p>
                      <p className="font-lato not-italic font-normal text-[12px] leading-[18px] text-center text-[#475467] mb-3">
                        No coach is currently assigned. Click the "+ Assign Coach" icon to select one.
                      </p>
                      <button
                        type="button"
                        onClick={() => setFieldValue("coachIds", [])}
                        className="px-4 py-2 rounded-lg font-lato not-italic font-medium text-[14px] leading-[150%] text-white flex items-center gap-2"
                        style={{ backgroundColor: '#FF8E1C' }}
                      >
                        Assign Coach
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 3v10M3 8h10" stroke="white" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </TabContent>
            </Tabs>

            <div className="flex dailog-footer">
              <button
                type="button"
                className="mr-1.5 whiteBtnShadow btn border border-[#FFF4E5] block w-full rounded-lg h-10 bg-white font-lato not-italic font-medium text-[14px] leading-[150%] text-[#344054] hover:bg-[#FFF4E5] hover:border-[#FFF4E5] duration-100 secondary-btn"
                onClick={() => handleDialogClose(resetForm)}
                style={{ backgroundColor: '#FFF4E5' }}
              >
                Cancel
              </button>

              {(tab === "tab1" || tab === "tab2" || tab === "tab3") && (
                <BtnPrimary
                  type="button"
                  onClick={() => handleNext(validateForm, values, setTouched, setFieldError)}
                  className="ml-1.5"
                  style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                >
                  Next
                </BtnPrimary>
              )}

              {tab === "tab4" && (
                <BtnPrimary
                  type="submit"
                  disabled={isSubmitting}
                  className="ml-1.5"
                  style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                >
                  {isSubmitting ? <ThreeDotLoader /> : (isEdit ? "Update Company" : "Add Company")}
                </BtnPrimary>
              )}
            </div>
          </Form>

        </Dialog>
      )}
    </Formik>
  );
};

export default AddEditCompany;
